import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Upload, Button, Card, Row, Col, Input, Progress, Space, Typography, Tag, message
} from 'antd';
import {
  InboxOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined,
  PictureOutlined, VideoCameraOutlined, ReloadOutlined
} from '@ant-design/icons';

import { uploadImage, uploadVideo } from '../utils/caminoUploads';

const { Text } = Typography;
const { Dragger } = Upload;

const DEFAULT_FOLDER = 'camino-2026';

// Limpia el item para entregarlo al padre: solo lo que se persiste.
const toMedia = (it) => ({
  url: it.url,
  type: it.type,
  caption: it.caption || ''
});

// Detecta el tipo desde mime.
const detectType = (file) => (file.type || '').startsWith('video/') ? 'video' : 'image';

const BulkMediaUploader = ({ value = [], onChange, folder = DEFAULT_FOLDER, maxFiles = 60 }) => {
  // items contiene tanto los persistidos (con url) como los en curso (con file/progress).
  // Cada item: { localId, type, url?, caption, progress, status, error?, fileName? }
  const [items, setItems] = useState(() => value.map((m, i) => ({
    localId: `init-${i}`,
    type: m.type,
    url: m.url,
    caption: m.caption || '',
    progress: 100,
    status: 'done',
    fileName: m.url ? m.url.split('/').pop() : ''
  })));

  // Reflejar cambios externos del padre (p. ej. carga inicial al editar galería).
  // Solo aplicamos si el value es realmente diferente.
  const valueKey = JSON.stringify(value);
  useEffect(() => {
    setItems(curr => {
      const externalKey = valueKey;
      const localKey = JSON.stringify(curr.filter(i => i.status === 'done').map(toMedia));
      if (externalKey === localKey) return curr;
      return value.map((m, i) => ({
        localId: `ext-${i}-${Date.now()}`,
        type: m.type,
        url: m.url,
        caption: m.caption || '',
        progress: 100,
        status: 'done',
        fileName: m.url ? m.url.split('/').pop() : ''
      }));
    });
  }, [valueKey, value]);

  // Notificar al padre con solo los items terminados.
  const lastNotified = useRef('');
  useEffect(() => {
    const done = items.filter(i => i.status === 'done').map(toMedia);
    const key = JSON.stringify(done);
    if (key !== lastNotified.current) {
      lastNotified.current = key;
      onChange?.(done);
    }
  }, [items, onChange]);

  const updateOne = useCallback((localId, patch) => {
    setItems(curr => curr.map(it => it.localId === localId ? { ...it, ...patch } : it));
  }, []);

  const removeOne = useCallback((localId) => {
    setItems(curr => curr.filter(it => it.localId !== localId));
  }, []);

  const move = useCallback((localId, dir) => {
    setItems(curr => {
      const idx = curr.findIndex(it => it.localId === localId);
      if (idx < 0) return curr;
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= curr.length) return curr;
      const next = [...curr];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  }, []);

  // Inicia la subida de un item.
  const startUpload = useCallback(async (localId, file) => {
    const type = detectType(file);
    updateOne(localId, { status: 'uploading', progress: 0, error: null });
    try {
      const url = type === 'video'
        ? await uploadVideo(file, (p) => updateOne(localId, { progress: p }))
        : await uploadImage(file, (p) => updateOne(localId, { progress: p }));
      updateOne(localId, { url, status: 'done', progress: 100 });
    } catch (error) {
      const msg = error?.response?.data?.error || error?.message || 'Error desconocido';
      updateOne(localId, { status: 'error', error: msg });
      message.error(`Error subiendo ${file.name}: ${msg}`);
    }
  }, [updateOne]);

  // Reintento de un item fallido.
  const retry = useCallback((item) => {
    if (!item._file) {
      message.error('No se puede reintentar (archivo no disponible). Sube de nuevo.');
      return;
    }
    startUpload(item.localId, item._file);
  }, [startUpload]);

  // Cantidad actual de items, accesible de forma síncrona desde handlers.
  const itemsCountRef = useRef(items.length);
  useEffect(() => { itemsCountRef.current = items.length; }, [items.length]);

  // Maneja la lista de archivos seleccionados (drag o picker).
  // IMPORTANTE: side-effects (validación, message.*, startUpload) van FUERA del
  // updater de setItems para no duplicarse con React Strict Mode.
  const handleNewFiles = useCallback((files) => {
    const arr = Array.from(files);
    if (!arr.length) return;

    // Validación + filtro fuera de setItems.
    const validMime = arr.filter(f => {
      const t = (f.type || '').toLowerCase();
      if (!t.startsWith('image/') && !t.startsWith('video/')) {
        message.error(`${f.name}: tipo no soportado`);
        return false;
      }
      return true;
    });
    if (!validMime.length) return;

    // Recorte por maxFiles usando el conteo actual.
    const currentCount = itemsCountRef.current;
    const room = Math.max(0, maxFiles - currentCount);
    if (validMime.length > room) {
      message.warning(`Máximo ${maxFiles} archivos por galería. Algunos se han ignorado.`);
    }
    const accepted = validMime.slice(0, room);
    if (!accepted.length) return;

    const newItems = accepted.map(file => ({
      localId: `up-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: detectType(file),
      caption: '',
      progress: 0,
      status: 'uploading',
      fileName: file.name,
      _file: file
    }));

    // setItems con updater puro (sin side effects).
    setItems(curr => [...curr, ...newItems]);

    // Side effects fuera de setItems.
    newItems.forEach(it => startUpload(it.localId, it._file));
  }, [maxFiles, startUpload]);

  // Dedup de beforeUpload: antd llama una vez por archivo en el batch.
  // Procesamos el batch completo cuando vemos un fileList nuevo.
  const lastBatchRef = useRef(null);
  const draggerProps = {
    multiple: true,
    showUploadList: false,
    accept: 'image/*,video/*',
    beforeUpload: (file, fileList) => {
      if (lastBatchRef.current !== fileList) {
        lastBatchRef.current = fileList;
        handleNewFiles(fileList);
      }
      return Upload.LIST_IGNORE;
    }
  };

  const uploadingCount = items.filter(i => i.status === 'uploading').length;
  const errorCount = items.filter(i => i.status === 'error').length;
  const doneCount = items.filter(i => i.status === 'done').length;

  return (
    <div>
      <Dragger {...draggerProps} style={{ marginBottom: 16 }}>
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p className="ant-upload-text">Suelta aquí imágenes y videos o haz clic para seleccionar</p>
        <p className="ant-upload-hint">
          Múltiples archivos. Se suben a <code>{folder}/</code> en Bunny.
        </p>
      </Dragger>

      {items.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Space size="small">
            <Tag color="blue">{items.length} elementos</Tag>
            {doneCount > 0 && <Tag color="green">{doneCount} subidos</Tag>}
            {uploadingCount > 0 && <Tag color="orange">{uploadingCount} subiendo</Tag>}
            {errorCount > 0 && <Tag color="red">{errorCount} con error</Tag>}
          </Space>
        </div>
      )}

      <Row gutter={[12, 12]}>
        {items.map((it, idx) => (
          <Col xs={24} sm={12} md={8} lg={6} key={it.localId}>
            <Card
              size="small"
              styles={{ body: { padding: 8 } }}
              cover={
                <div style={{
                  width: '100%', aspectRatio: '16 / 9', background: '#f0f0f0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden'
                }}>
                  {it.status === 'done' && it.type === 'image' && it.url ? (
                    <img src={it.url} alt={it.fileName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : it.status === 'done' && it.type === 'video' && it.url ? (
                    <div style={{ textAlign: 'center', color: '#666' }}>
                      <VideoCameraOutlined style={{ fontSize: 40 }} />
                      <div style={{ fontSize: 11 }}>Video</div>
                    </div>
                  ) : it.type === 'video' ? (
                    <VideoCameraOutlined style={{ fontSize: 40, color: '#999' }} />
                  ) : (
                    <PictureOutlined style={{ fontSize: 40, color: '#999' }} />
                  )}
                </div>
              }
            >
              <div style={{ minHeight: 60 }}>
                <Text ellipsis style={{ fontSize: 12, display: 'block' }} title={it.fileName}>
                  {it.fileName}
                </Text>

                {it.status === 'uploading' && (
                  <Progress percent={it.progress} size="small" />
                )}
                {it.status === 'error' && (
                  <div>
                    <Text type="danger" style={{ fontSize: 11 }}>
                      {it.error || 'Error'}
                    </Text>
                  </div>
                )}
                {it.status === 'done' && (
                  <Input
                    size="small"
                    placeholder="Caption (opcional)"
                    value={it.caption}
                    onChange={(e) => updateOne(it.localId, { caption: e.target.value })}
                    maxLength={140}
                    style={{ fontSize: 12, marginTop: 4 }}
                  />
                )}
              </div>

              <Space size={4} style={{ marginTop: 6, width: '100%', justifyContent: 'space-between' }}>
                <Space size={2}>
                  <Button size="small" icon={<ArrowUpOutlined />}
                    disabled={idx === 0} onClick={() => move(it.localId, -1)} />
                  <Button size="small" icon={<ArrowDownOutlined />}
                    disabled={idx === items.length - 1} onClick={() => move(it.localId, +1)} />
                </Space>
                <Space size={2}>
                  {it.status === 'error' && (
                    <Button size="small" icon={<ReloadOutlined />} onClick={() => retry(it)} />
                  )}
                  <Button size="small" danger icon={<DeleteOutlined />}
                    onClick={() => removeOne(it.localId)} />
                </Space>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default BulkMediaUploader;
